// src/hooks/useSecureAuth.js
import { useState, useCallback, useEffect } from 'react';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import axios from 'axios';

const SESSION_STORAGE_KEY = 'tb_session_active';

export const useSecureAuth = () => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detectedUser, setDetectedUser] = useState(null); // For One-Tap detected user
    const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

    // Check session status when component mounts
    useEffect(() => {
        const checkSession = async () => {
            // Check if this tab has an active session (sessionStorage)
            const hasTabSession = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
            
            if (!hasTabSession) {
                // New tab - clear any existing cookie and show login
                try {
                    await axios.post('https://qa-tenantbridge.parablu.com/api/auth/logout', {}, {
                        withCredentials: true
                    });
                } catch (error) {
                    // Ignore errors - cookie might not exist
                }
                setUser(null);
                setIsAuthenticated(false);
                setLoading(false);
                return;
            }

            // Tab has session flag - verify with backend
            try {
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/auth/session', {
                    withCredentials: true // Important for cookies
                });

                if (response.data.isAuthenticated) {
                    setUser(response.data.user);
                    setIsAuthenticated(true);
                } else {
                    // Session not valid, clear sessionStorage and ensure clean state
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } catch (error) {
                // 401 means token expired - clear state immediately
                if (error.response?.status === 401) {
                    // Clear sessionStorage immediately
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    // Clear auth state immediately
                    setUser(null);
                    setIsAuthenticated(false);
                    setAuthError('Session expired. Please sign in again.');
                    // The interceptor will also dispatch auth:sessionExpired event
                    // but we've already cleared state above, so it's safe
                } else {
                    console.error('Session check failed:', error);
                    // Other errors - clear sessionStorage and ensure clean state
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } finally {
                setLoading(false);
            }
        };

        checkSession();
    }, []);

    // Listen for session expiration events from API interceptor
    useEffect(() => {
        const handleSessionExpired = async () => {
            // Clear sessionStorage
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            
            // Clear auth state
            setUser(null);
            setIsAuthenticated(false);
            setAuthError('Session expired. Please sign in again.');
            setShowWelcomeMessage(false);
            
            // Clear any cookies
            try {
                await axios.post('https://qa-tenantbridge.parablu.com/api/auth/logout', {}, {
                    withCredentials: true
                });
            } catch (error) {
                console.error('Logout on session expiry failed:', error);
            }
        };

        window.addEventListener('auth:sessionExpired', handleSessionExpired);
        return () => {
            window.removeEventListener('auth:sessionExpired', handleSessionExpired);
        };
    }, []);

    // Google One-Tap for automatic user detection
    useGoogleOneTapLogin({
        onSuccess: async (credentialResponse) => {
            try {
                // Decode the JWT token to get user info
                const base64Url = credentialResponse.credential.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const userInfo = JSON.parse(jsonPayload);

                // Check if it's a CrashPlan email
                if (userInfo.email && userInfo.email.endsWith('@crashplan.com')) {
                    setDetectedUser({
                        name: userInfo.name,
                        email: userInfo.email,
                        picture: userInfo.picture
                    });
                }
            } catch (error) {
                console.error('One-Tap user detection error:', error);
            }
        },
        onError: (error) => {
            console.log('One-Tap error (this is normal if user is not signed in):', error);
        },
        auto_select: true,
        cancel_on_tap_outside: false
    });

    const googleLogin = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                setLoading(true);
                // Send the Google token to our backend
                const response = await axios.post(
                    'https://qa-tenantbridge.parablu.com/api/auth/google',
                    { token: tokenResponse.access_token },
                    { withCredentials: true } // Important for cookies
                );

                if (response.status === 200) {
                    // Set sessionStorage flag for this tab
                    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
                    setUser(response.data.user);
                    setIsAuthenticated(true);
                    setAuthError(null);
                    // Show welcome message on successful login
                    setShowWelcomeMessage(true);
                }
            } catch (error) {
                console.error('Authentication error:', error);
                // Handle domain restriction error specifically
                if (error.response?.status === 403) {
                    setAuthError('Access restricted to @crashplan.com accounts only');
                } else if (error.response?.status === 404) {
                    // User not found in database
                    setAuthError(error.response?.data?.message || 'Please contact tenantBridge Team');
                } else {
                    setAuthError(error.response?.data?.message || 'Authentication failed');
                }
                setUser(null);
                setIsAuthenticated(false);
            } finally {
                setLoading(false);
            }
        },
        onError: (error) => {
            console.error('Login Failed:', error);
            setAuthError('Google authentication failed');
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
        },
        scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email'
    });

    const handleLogin = useCallback(() => {
        googleLogin();
    }, [googleLogin]);

    const handleLogout = useCallback(async () => {
        try {
            setLoading(true);
            // Clear sessionStorage
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            await axios.post('https://qa-tenantbridge.parablu.com/api/auth/logout', {}, {
                withCredentials: true
            });
            setUser(null);
            setIsAuthenticated(false);
            setAuthError(null);
            setShowWelcomeMessage(false);
            // Redirect to logout confirmation page
            window.location.href = '/logout';
        } catch (error) {
            console.error('Logout error:', error);
            // Clear sessionStorage even if API call fails
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            // Still redirect even if logout API call fails
            window.location.href = '/logout';
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        user,
        detectedUser,
        authError,
        handleLogin,
        handleLogout,
        isAuthenticated,
        loading,
        showWelcomeMessage,
        setShowWelcomeMessage
    };
};